import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

interface WorkoutCalendarProps {
  workoutDates: string[];
  isLoading?: boolean;
}

export function WorkoutCalendar({ workoutDates, isLoading }: WorkoutCalendarProps) {
  const days = generateLast30Days();
  // Convert ISO timestamps to local date strings for matching
  const workoutSet = new Set(
    workoutDates.map((dateStr) => formatLocalDateStr(new Date(dateStr)))
  );

  return (
    <Card className="mb-6">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg">Activity</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="grid grid-cols-7 gap-1">
            {Array.from({ length: 35 }).map((_, i) => (
              <div
                key={i}
                className="aspect-square rounded-sm bg-muted animate-pulse"
              />
            ))}
          </div>
        ) : (
          <>
            {/* Day labels */}
            <div className="grid grid-cols-7 gap-1 mb-1">
              {["M", "T", "W", "T", "F", "S", "S"].map((day, i) => (
                <div
                  key={i}
                  className="text-xs text-muted-foreground text-center"
                >
                  {day}
                </div>
              ))}
            </div>
            {/* Calendar grid */}
            <div className="grid grid-cols-7 gap-1">
              {days.map((day) => {
                const isWorkout = workoutSet.has(day.dateStr);
                const isToday = day.dateStr === formatLocalDateStr(new Date());

                return (
                  <div
                    key={day.dateStr}
                    className={`
                      aspect-square rounded-sm flex items-center justify-center text-xs relative
                      ${day.isFuture ? "bg-muted/30" : isWorkout ? "bg-green-500" : "bg-muted/50"}
                      ${isToday ? "ring-2 ring-yellow-500" : ""}
                    `}
                    title={day.isFuture ? `${day.label} - Future` : `${day.label}${isWorkout ? " - Workout" : ""}`}
                  >
                    <span className={
                      day.isFuture
                        ? "text-muted-foreground/40"
                        : isWorkout
                          ? "text-white font-medium"
                          : "text-muted-foreground"
                    }>
                      {day.dayNum}
                    </span>
                    {day.isFuture && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-full h-px bg-muted-foreground/30 rotate-45 transform origin-center" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {/* Legend */}
            <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-sm bg-green-500" />
                <span>Workout</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-sm bg-muted/50" />
                <span>Rest</span>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

interface DayInfo {
  dateStr: string;
  dayNum: number;
  label: string;
  isFuture: boolean;
}

function generateLast30Days(): DayInfo[] {
  const days: DayInfo[] = [];
  const today = new Date();
  const todayStr = formatLocalDateStr(today);

  // Find the Monday of the current week
  const currentWeekMonday = new Date(today);
  const todayDay = today.getDay();
  const daysFromMonday = todayDay === 0 ? 6 : todayDay - 1;
  currentWeekMonday.setDate(today.getDate() - daysFromMonday);

  // Go back 4 more weeks from current week's Monday (5 weeks total)
  const startDate = new Date(currentWeekMonday);
  startDate.setDate(currentWeekMonday.getDate() - 28);

  // Generate 35 days (5 weeks), ensuring current week is always the last row
  for (let i = 0; i < 35; i++) {
    const date = new Date(startDate);
    date.setDate(startDate.getDate() + i);

    const dateStr = formatLocalDateStr(date);
    const isFuture = dateStr > todayStr;

    days.push({
      dateStr,
      dayNum: date.getDate(),
      label: date.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }),
      isFuture,
    });
  }

  return days;
}

// Format date as YYYY-MM-DD using LOCAL timezone (not UTC)
function formatLocalDateStr(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}
